/**
 * Signup Slice - Redux state management for multi-step signup
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Signup flow state
  currentStep: 'email', // email, verify, company, plan, admin, complete
  signupToken: null,
  email: null,

  // Step-specific data
  emailVerified: false,
  company: {
    name: null,
    website: null,
    industry: null,
    size: null,
    slug: null,
  },
  plan: {
    selected: null, // trial, professional, enterprise
    useDemoData: true,
  },
  admin: {
    firstName: null,
    lastName: null,
    useWebAuthn: false,
  },

  // UI state
  loading: false,
  error: null,
  successMessage: null,

  // Completion
  isComplete: false,
  user: null,
  tenant: null,
  tokens: {
    access: null,
    refresh: null,
  },
};

const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    // Step progression
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
      state.error = null;
    },

    // Email step
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    setSignupToken: (state, action) => {
      state.signupToken = action.payload;
    },
    setEmailVerified: (state, action) => {
      state.emailVerified = action.payload;
    },

    // Company step
    setCompanyDetails: (state, action) => {
      state.company = { ...state.company, ...action.payload };
    },

    // Plan step
    setPlan: (state, action) => {
      state.plan = { ...state.plan, ...action.payload };
    },

    // Admin step
    setAdminDetails: (state, action) => {
      state.admin = { ...state.admin, ...action.payload };
    },

    // Loading & errors
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Completion
    setSignupComplete: (state, action) => {
      state.isComplete = true;
      state.user = action.payload.user;
      state.tenant = action.payload.tenant;
      state.tokens = action.payload.tokens;
      state.loading = false;
    },

    // Reset
    resetSignup: (_state) => {
      return initialState;
    },
  },
});

export const {
  setCurrentStep,
  setEmail,
  setSignupToken,
  setEmailVerified,
  setCompanyDetails,
  setPlan,
  setAdminDetails,
  setLoading,
  setError,
  clearError,
  setSuccessMessage,
  clearSuccessMessage,
  setSignupComplete,
  resetSignup,
} = signupSlice.actions;

// Selectors
export const selectSignupStep = (state) => state.signup.currentStep;
export const selectSignupEmail = (state) => state.signup.email;
export const selectSignupToken = (state) => state.signup.signupToken;
export const selectEmailVerified = (state) => state.signup.emailVerified;
export const selectCompanyDetails = (state) => state.signup.company;
export const selectPlanSelection = (state) => state.signup.plan;
export const selectAdminDetails = (state) => state.signup.admin;
export const selectSignupLoading = (state) => state.signup.loading;
export const selectSignupError = (state) => state.signup.error;
export const selectSignupSuccess = (state) => state.signup.successMessage;
export const selectSignupIsComplete = (state) => state.signup.isComplete;
export const selectSignupUser = (state) => state.signup.user;
export const selectSignupTenant = (state) => state.signup.tenant;
export const selectSignupTokens = (state) => state.signup.tokens;

export default signupSlice.reducer;
